"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import {
  Activity,
  BarChart3,
  BookTemplate,
  ChevronDown,
  CircleDollarSign,
  FileSearch,
  FolderKanban,
  LayoutDashboard,
  LogOut,
  Menu,
  Settings2,
  ShieldCheck,
  UserRoundCog,
  UsersRound,
  X,
} from "lucide-react";
import { Brand } from "@/components/brand";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import type { SessionContext } from "@/lib/phaseone";
import { roleLabels } from "@/lib/phaseone";

const appNav = [
  { href: "/app", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { href: "/app/projects", label: "Projects", icon: FolderKanban },
  { href: "/app/billing", label: "Billing", icon: CircleDollarSign },
  { href: "/app/settings/profile", label: "Profile & security", icon: Settings2 },
];

const adminNav = [
  { href: "/admin", label: "Operations", icon: Activity, exact: true },
  { href: "/admin/users", label: "Users", icon: UsersRound },
  { href: "/admin/templates", label: "Templates", icon: BookTemplate },
  { href: "/admin/sources", label: "Source connectors", icon: FileSearch },
  { href: "/admin/content", label: "Content", icon: UserRoundCog },
  { href: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { href: "/admin/audit", label: "Audit", icon: ShieldCheck },
];

function initials(name: string) {
  return name.split(" ").slice(0, 2).map((part) => part[0]).join("").toUpperCase();
}

export function WorkspaceShell({ children, context, admin = false }: { children: React.ReactNode; context: SessionContext; admin?: boolean }) {
  const pathname = usePathname();
  const router = useRouter();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pending, startTransition] = useTransition();
  const nav = admin ? adminNav : appNav;

  function signOut() {
    startTransition(async () => {
      await fetch("/api/preview/session", { method: "DELETE" });
      router.push("/");
      router.refresh();
    });
  }

  const sidebar = (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-5">
        <Brand className="text-sidebar-foreground" />
        <Button variant="ghost" size="icon-sm" className="text-sidebar-foreground hover:bg-sidebar-accent lg:hidden" onClick={() => setMobileOpen(false)} aria-label="Close navigation"><X aria-hidden="true" /></Button>
      </div>
      <div className="px-3 py-4">
        <div className="rounded-xl border border-sidebar-border bg-white/4 p-3">
          <div className="flex items-center gap-3">
            <Avatar><AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground">{initials(context.fullName)}</AvatarFallback></Avatar>
            <div className="min-w-0"><p className="truncate text-xs font-semibold">{context.fullName}</p><p className="mt-0.5 truncate text-[0.66rem] text-sidebar-foreground/70">{context.organizationName}</p></div>
          </div>
          <Badge variant="outline" className="mt-3 border-sidebar-border bg-white/5 font-mono text-[0.58rem] tracking-wide text-sidebar-foreground/70 uppercase">{roleLabels[context.role]}</Badge>
        </div>
      </div>
      <nav className="grid gap-1 px-3" aria-label={admin ? "Admin navigation" : "Workspace navigation"}>
        <p className="px-2 pb-2 font-mono text-[0.6rem] tracking-[0.14em] text-sidebar-foreground/70 uppercase">{admin ? "Organization admin" : "Evidence workspace"}</p>
        {nav.map((item) => {
          const active = item.exact ? pathname === item.href : pathname.startsWith(item.href);
          return <Link key={item.href} href={item.href} onClick={() => setMobileOpen(false)} className={cn(buttonVariants({ variant: "ghost" }), "h-9 justify-start gap-2.5 px-2.5 text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground", active && "bg-sidebar-accent text-sidebar-accent-foreground")}><item.icon aria-hidden="true" />{item.label}</Link>;
        })}
        {context.role === "org_admin" && !admin && <><Separator className="my-3 bg-sidebar-border" /><Link href="/admin" className={cn(buttonVariants({ variant: "ghost" }), "h-9 justify-start gap-2.5 px-2.5 text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground")}><ShieldCheck aria-hidden="true" />Admin console</Link></>}
        {admin && <><Separator className="my-3 bg-sidebar-border" /><Link href="/app" className={cn(buttonVariants({ variant: "ghost" }), "h-9 justify-start gap-2.5 px-2.5 text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground")}><LayoutDashboard aria-hidden="true" />Back to workspace</Link></>}
      </nav>
      <div className="mt-auto border-t border-sidebar-border p-3">
        <Button variant="ghost" className="w-full justify-between text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground" onClick={signOut} disabled={pending}><span className="flex items-center gap-2"><LogOut aria-hidden="true" />{pending ? "Signing out…" : "Sign out"}</span><ChevronDown className="-rotate-90" aria-hidden="true" /></Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 lg:block">{sidebar}</aside>
      {mobileOpen && <div className="fixed inset-0 z-50 lg:hidden"><button className="absolute inset-0 bg-black/35 backdrop-blur-sm" onClick={() => setMobileOpen(false)} aria-label="Close navigation overlay" /><aside className="relative h-full w-[min(20rem,86vw)]">{sidebar}</aside></div>}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-background/86 px-4 backdrop-blur-xl sm:px-6 lg:px-8">
          <div className="flex items-center gap-3"><Button variant="outline" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Open navigation"><Menu aria-hidden="true" /></Button><div><p className="text-xs font-semibold">{admin ? "Organization control room" : "PhaseOne workspace"}</p><p className="hidden text-[0.66rem] text-muted-foreground sm:block">Synthetic preview · no client records</p></div></div>
          <div className="flex items-center gap-2"><span className="hidden text-right sm:block"><span className="block text-xs font-semibold">{context.fullName}</span><span className="block text-[0.64rem] text-muted-foreground">{context.jobTitle}</span></span><Avatar size="sm"><AvatarFallback>{initials(context.fullName)}</AvatarFallback></Avatar></div>
        </header>
        <main className="workspace-grid min-h-[calc(100vh-4rem)]">{children}</main>
      </div>
    </div>
  );
}
